import React, {useCallback, useMemo, useState, useEffect} from 'react';
import {useParams, useNavigate, useLocation} from 'react-router-dom';
import {Card} from 'react-bootstrap';
import {PageTitle} from '../../../../_metronic/layout/core';
import Level3Title from '../../../../app/common/components/title/Level3Title';
import TableMetronic from '../../../../app/common/components/table/TableMetronic';
import {ColumnDef} from '@tanstack/react-table';
import clsx from 'clsx';
import AddMailingListModal from './modals/AddMailingListModal';
import EditMailingListModal from './modals/EditMailingListModal';
import DeleteMailingListModal from './modals/DeleteMailingListModal';
import styles from '../../Styles/Email-accounts/DomainMailingLists.module.scss';
import {isNotFoundError, NOT_FOUND_ROUTE} from '../../../../app/common/tools/httpError';
import {createMailingList, deleteMailingList, getMailinglists, updateMailingList} from '../../Api/domainEmailAccounts';

interface MailingList {
  id: string;
  adresse: string;
  membres?: string[];
}

const DomainMailingLists = (): JSX.Element => {
  const {domainName} = useParams<{domainName: string}>();
  const navigate = useNavigate();
  const location = useLocation();

  const [mailingLists, setMailingLists] = useState<MailingList[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [currentList, setCurrentList] = useState<MailingList | null>(null);

  useEffect(() => {
    let mounted = true;
    const fetchLists = async () => {
      setIsLoading(true);
      setError(null);
      try {
        const data = await getMailinglists(domainName || 'example.com');

        const lists: MailingList[] = Array.isArray(data)
          ? data.map((item: any) => ({
              id: item.id,
              adresse: item.name || '',
              membres: item.addresses || [],
            }))
          : [];

        if (mounted) setMailingLists(lists);
      } catch (e) {
        if (isNotFoundError(e)) {
          navigate(NOT_FOUND_ROUTE, {replace: true});
          return;
        }
        console.error('Erreur getMailinglists:', e);
        if (mounted) setError('Erreur lors du chargement des listes de diffusion');
      } finally {
        if (mounted) setIsLoading(false);
      }
    };
    fetchLists();
    return () => {
      mounted = false;
    };
  }, [domainName, navigate]);

  const handleEdit = useCallback((list: MailingList) => {
    setCurrentList(list);
    setShowEditModal(true);
  }, []);

  const handleSave = async (updatedList: MailingList) => {
    if (!currentList) return;

    try {
      const newAdresse = updatedList.adresse;

      console.log("currentList:", currentList);
      console.log("updatedList:", updatedList);
      console.log("newAccountLocal:", newAdresse);
      
      if (currentList.id !== newAdresse) {
        await updateMailingList(domainName || '', {
          oldAccountName: currentList.id,
          account: newAdresse
        });
      }

      // Refresh data from server
      const data = await getMailinglists(domainName || 'example.com');
      const lists: MailingList[] = Array.isArray(data)
        ? data.map((item: any) => ({
            id: item.id,
            adresse: item.name || '',
            membres: item.addresses || [],
          }))
        : [];
      setMailingLists(lists);

      console.log('Liste modifiée avec succès:', updatedList);
    } catch (error) {
      console.error('Erreur lors de la modification de la liste:', error);
      throw error;
    }
  };

  const handleDeleteClick = useCallback((list: MailingList) => {
    setCurrentList(list);
    setShowDeleteModal(true);
  }, []);

  const handleDeleteConfirm = async () => {
    if (!currentList) return;
    setIsLoading(true);
    setError(null);
    try {
      await deleteMailingList(domainName || '', currentList.id);

      // Refresh data from server
      const data = await getMailinglists(domainName || '');
      const lists: MailingList[] = Array.isArray(data)
        ? data.map((item: any) => ({
            id: item.id,
            adresse: item.name || '',
            membres: item.addresses || [],
          }))
        : [];
      setMailingLists(lists);

      console.log('Liste supprimée:', currentList);
      setShowDeleteModal(false);
    } catch (err) {
      console.error('Erreur lors de la suppression de la liste:', err);
      setError('Erreur lors de la suppression de la liste de diffusion');
    } finally {
      setIsLoading(false);
    }
  };

  const handleAdd = async (adresse: string) => {
    try {
      const accountLocal = adresse.split('@')[0];
      console.log("accountLocal:", accountLocal);
      
      await createMailingList(domainName || '', {
        account: accountLocal
      });

      // Refresh data from server
      const data = await getMailinglists(domainName || '');
      const lists: MailingList[] = Array.isArray(data)
        ? data.map((item: any) => ({
            id: item.id,
            adresse: item.name || '',
            membres: item.addresses || [],
          }))
        : [];
      setMailingLists(lists);

      console.log('Liste de diffusion créée:', adresse);
    } catch (error) {
      console.error('Erreur lors de la création de la liste de diffusion:', error);
      throw error;
    }
  };

  const columns = useMemo<ColumnDef<MailingList>[]>(
    () => [
      {
        header: 'Adresse de la liste',
        accessorKey: 'adresse',
        cell: (info) => (
          <span
            className="fw-bold text-primary cursor-pointer"
            onClick={() => {
              const list = info.row.original;
              navigate(
                `/services/domain/emails/${domainName}/lists/${list.id}/members${location.search}`,
                {
                  state: {
                    ...(location.state as object || {}),
                    mailingList: list,
                  }
                }
              );
            }}
            style={{cursor: 'pointer'}}
          >
            {info.getValue() as string}
          </span>
        ),
      },
      {
        header: 'Nombre de membres',
        accessorKey: 'membres',
        cell: (info) => {
          const membres = info.getValue() as string[];
          return (
            <span className="badge badge-light-info">
              {membres?.length || 0} membre{(membres?.length || 0) > 1 ? 's' : ''}
            </span>
          );
        },
      },
      {
        header: 'Actions',
        accessorKey: 'id',
        cell: (info) => (
          <div className="d-flex gap-2">
            <button
              className="btn btn-sm btn-icon btn-light-primary"
              onClick={() => handleEdit(info.row.original)}
              title="Modifier"
            >
              <i className="fa-solid fa-pen fs-6"></i>
            </button>
            <button
              className="btn btn-sm btn-icon btn-light-danger"
              onClick={() => handleDeleteClick(info.row.original)}
              title="Supprimer"
            >
              <i className="fa-solid fa-trash fs-6"></i>
            </button>
          </div>
        ),
      },
    ],
    [domainName, location.search, location.state, navigate, handleEdit, handleDeleteClick]
  );

  return (
    <>
      <PageTitle breadcrumbs={[]}>{`Listes de diffusion - ${domainName}`}</PageTitle>
        <div className={clsx(styles.containerDomainMailingLists)}>
          <Card>
            <Card.Header>
              <Card.Title className="d-flex justify-content-between align-items-center w-100">
                <Level3Title title={`Listes de diffusion`} />
                <button 
                  className="btn btn-sm btn-primary"
                  onClick={() => setShowAddModal(true)}
                >
                  <i className="fa-solid fa-plus me-2"></i>
                  Ajouter une liste
                </button>
              </Card.Title>
            </Card.Header>
            <Card.Body>
              <TableMetronic<MailingList>
                data={mailingLists}
                columns={columns}
                isLoading={isLoading}
                showCardHeader={false}
                searchBar={false}
                pagination
                defaultSorting={[
                  {
                    id: 'adresse',
                    desc: false,
                  },
                ]}
              />
              {error && <div className="text-danger mt-2">{error}</div>}
            </Card.Body>
          </Card>
        </div>

        {/* Modal d'ajout */}
        <AddMailingListModal
          show={showAddModal}
          onHide={() => setShowAddModal(false)}
          onAdd={handleAdd}
          domainName={domainName || 'example.com'}
        />

        {/* Modal de modification */}
        <EditMailingListModal
          show={showEditModal}
          onHide={() => setShowEditModal(false)}
          onSave={handleSave}
          mailingList={currentList}
          domainName={domainName || 'example.com'}
        />

        {/* Modal de suppression */}
        <DeleteMailingListModal
          show={showDeleteModal}
          onHide={() => setShowDeleteModal(false)}
          onConfirm={handleDeleteConfirm}
          mailingList={currentList}
        />
    </>
  );
};

export default DomainMailingLists;
